/**
 * cart-notification.js (Prestige 테마 대응 버전)
 *
 * 방어 전략:
 * 1. form submit 가로채기 (표준)
 * 2. cart:updated / cart:item-added / variant:added 등 Prestige 커스텀 이벤트 리스닝
 * 3. fetch 패치 (최후 수단) - /cart/add.js 호출 가로채기
 * 4. 디버그 모드: window.__CART_NOTIF_DEBUG = true 면 이벤트 로깅
 */
(function () {
  'use strict';

  const root = document.getElementById('CartNotification');
  if (!root) {
    console.warn('[cart-notification] CartNotification element not found. Section may not be loaded.');
    return;
  }

  const config = {
    autoDismissMs: parseInt(root.dataset.autoDismissMs, 10) || 4000,
    mobileBreakpoint: parseInt(root.dataset.mobileBreakpoint, 10) || 768,
    recsLimit: parseInt(root.dataset.recommendationsLimit, 10) || 10,
    recsIntent: root.dataset.recommendationsIntent || 'related',
    fallbackCollection: root.dataset.fallbackCollection || '',
    msgAdded: root.dataset.msgAdded || 'Added to cart',
    msgUpdated: root.dataset.msgUpdated || 'Quantity updated to {qty}',
    msgErrorTitle: root.dataset.msgErrorTitle || 'Could not add',
    msgErrorStock: root.dataset.msgErrorStock || 'Out of stock',
    msgErrorNetwork: root.dataset.msgErrorNetwork || 'Network error',
    msgErrorOptions: root.dataset.msgErrorOptions || 'Please select options',
    msgViewCart: root.dataset.msgViewCart || 'View cart',
    msgRecsTitle: root.dataset.msgRecsTitle || 'You may also like',
    msgClose: root.dataset.msgClose || 'Close',
  };

  const toastContainer = root.querySelector('[data-toast-container]');
  const sheet = root.querySelector('[data-sheet]');
  const backdrop = root.querySelector('[data-sheet-backdrop]');

  root.hidden = false;

  const state = {
    toastTimer: null,
    toastEl: null,
    recsCache: new Map(),
    lastAddedAt: 0,
  };

  const DEBUG = window.__CART_NOTIF_DEBUG === true;
  function log(...args) {
    if (DEBUG) console.log('[cart-notification]', ...args);
  }

  function isMobile() {
    return window.innerWidth < config.mobileBreakpoint;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents); } catch (e) {}
    }
    return '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ===== DEFENSE LAYER 1: form submit interception =====
  document.addEventListener('submit', async function (e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.action || !form.action.includes('/cart/add')) return;

    log('form submit intercepted', form.action);
    e.preventDefault();
    e.stopPropagation();

    await handleAddRequest(form);
  }, true);

  async function handleAddRequest(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const originalBtnState = submitBtn ? submitBtn.disabled : false;
    if (submitBtn) submitBtn.disabled = true;

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        const errorMsg = errorBody.description || errorBody.message || config.msgErrorStock;
        const errType = res.status === 404 ? 'options' : 'stock';
        showError(errType, errorMsg);
        return;
      }

      const data = await res.json();
      await handleAddSuccess(data);
    } catch (err) {
      console.error('[cart-notification] add error:', err);
      showError('network', config.msgErrorNetwork);
    } finally {
      if (submitBtn) submitBtn.disabled = originalBtnState;
    }
  }

  async function handleAddSuccess(addData) {
    const now = Date.now();
    if (now - state.lastAddedAt < 500) {
      log('duplicate add suppressed');
      return;
    }
    state.lastAddedAt = now;

    try {
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await cartRes.json();
      const line = cart.items.find(i => i.variant_id === addData.variant_id);
      const finalQty = line ? line.quantity : addData.quantity;
      const isNew = finalQty === addData.quantity;

      updateCartCount(cart.item_count);

      const payload = {
        product: {
          title: addData.product_title || addData.title,
          image: addData.image || (addData.featured_image && addData.featured_image.url),
          variantTitle: addData.variant_title,
          handle: addData.handle || addData.product_handle,
          productId: addData.product_id,
        },
        qty: finalQty,
        isNew,
      };
      show(payload);
    } catch (err) {
      console.error('[cart-notification] post-add error:', err);
    }
  }

  function updateCartCount(count) {
    const selectors = [
      '[data-cart-count]', '.cart-count', '.cart-link__bubble-num',
      '#CartCount', '.header__cart-count', '[data-header-cart-count]',
    ];
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => { el.textContent = count; });
    });
  }

  // ===== DEFENSE LAYER 2: Prestige custom events =====
  const PRESTIGE_EVENTS = [
    'cart:item-added', 'cart:updated', 'cart:refresh',
    'variant:added', 'product:added',
    'ajaxCart.afterCartLoad', 'shopify:cart:update',
  ];

  PRESTIGE_EVENTS.forEach(eventName => {
    document.addEventListener(eventName, async (e) => {
      log('custom event fired:', eventName, e.detail);
      // az-coupon.js 의 미선택 상품 다시 담기 후 갱신 — 사용자가 담은 게 아니므로 알림 없음
      if (e.detail && e.detail.azSilent) return;
      if (e.detail && e.detail.item) {
        await handleAddSuccess(e.detail.item);
      } else {
        try {
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          if (cart.items && cart.items.length > 0) {
            const lastItem = cart.items[cart.items.length - 1];
            await handleAddSuccess(lastItem);
          }
        } catch (err) {
          console.error('[cart-notification] event fallback error:', err);
        }
      }
    });
  });

  // ===== DEFENSE LAYER 3: fetch interception =====
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const url = args[0];
    const urlStr = typeof url === 'string' ? url : (url && url.url) || '';
    // window.__azRestoring: az-coupon.js 가 결제 때 뺀 미선택 상품을 다시 담는 중 → 알림 없음
    const isAddRequest = urlStr.includes('/cart/add') && !window.__azRestoring;

    const promise = originalFetch.apply(this, args);

    if (isAddRequest) {
      log('fetch intercepted:', urlStr);
      promise.then(async res => {
        if (!res.ok) return;
        try {
          const clone = res.clone();
          const data = await clone.json();
          if (data && data.variant_id) {
            await handleAddSuccess(data);
          } else if (data && data.items) {
            const lastItem = data.items[data.items.length - 1];
            await handleAddSuccess(lastItem);
          }
        } catch (err) {
          log('fetch response parse error:', err);
        }
      }).catch(() => {});
    }

    return promise;
  };

  if (DEBUG) {
    const originalDispatch = EventTarget.prototype.dispatchEvent;
    EventTarget.prototype.dispatchEvent = function (event) {
      if (event && event.type && (
        event.type.includes('cart') ||
        event.type.includes('product') ||
        event.type.includes('variant')
      )) {
        console.log('[cart-notification:debug] event dispatched:', event.type, event.detail);
      }
      return originalDispatch.call(this, event);
    };
  }

  function show(data) {
    closeAll();
    if (isMobile()) showSheet(data);
    else showToast(data);
  }

  function showError(type, message) {
    closeAll();
    const errorData = { error: true, errorType: type, errorMessage: message || config.msgErrorStock };
    if (isMobile()) showSheet(errorData);
    else showToast(errorData);
  }

  function showToast(data) {
    const toast = document.createElement('div');
    toast.className = 'cart-notif__toast' + (data.error ? ' is-error' : '');
    toast.setAttribute('role', 'status');

    if (data.error) {
      toast.innerHTML = `
        <div class="cart-notif__toast-icon">!</div>
        <div class="cart-notif__toast-content">
          <div class="cart-notif__toast-error-title">${escapeHtml(config.msgErrorTitle)}</div>
          <div class="cart-notif__toast-error-msg">${escapeHtml(data.errorMessage)}</div>
        </div>
        <button type="button" class="cart-notif__toast-close" aria-label="${escapeHtml(config.msgClose)}">✕</button>
      `;
    } else {
      const statusText = data.isNew ? config.msgAdded : config.msgUpdated.replace('{qty}', data.qty);
      const thumbHtml = data.product.image ? `<img src="${escapeHtml(data.product.image)}" alt="" loading="lazy">` : '';
      const metaText = [data.product.title, data.product.variantTitle].filter(Boolean).join(' · ');
      toast.innerHTML = `
        <div class="cart-notif__toast-icon">✓</div>
        <div class="cart-notif__toast-thumb">${thumbHtml}</div>
        <div class="cart-notif__toast-content">
          <div class="cart-notif__toast-title">${escapeHtml(statusText)}</div>
          <div class="cart-notif__toast-meta">${escapeHtml(metaText)}</div>
        </div>
        <button type="button" class="cart-notif__toast-link" data-view-cart>${escapeHtml(config.msgViewCart)}</button>
        <button type="button" class="cart-notif__toast-close" aria-label="${escapeHtml(config.msgClose)}">✕</button>
        <div class="cart-notif__toast-progress" data-progress></div>
      `;
    }

    toastContainer.appendChild(toast);
    state.toastEl = toast;
    toast.querySelector('.cart-notif__toast-close').addEventListener('click', closeToast);
    const viewBtn = toast.querySelector('[data-view-cart]');
    if (viewBtn) viewBtn.addEventListener('click', () => { window.location.href = '/cart'; });
    requestAnimationFrame(() => toast.classList.add('is-visible'));

    const duration = data.error ? Math.max(config.autoDismissMs, 5000) : config.autoDismissMs;
    const progress = toast.querySelector('[data-progress]');
    if (progress) {
      progress.style.transition = `transform ${duration}ms linear`;
      requestAnimationFrame(() => { progress.style.transform = 'scaleX(0)'; });
    }
    state.toastTimer = setTimeout(closeToast, duration);

    if (progress) {
      toast.addEventListener('mouseenter', () => {
        if (state.toastTimer) clearTimeout(state.toastTimer);
        const computed = getComputedStyle(progress).transform;
        progress.style.transition = 'none';
        progress.style.transform = computed;
      });
      toast.addEventListener('mouseleave', () => {
        try {
          const m = new DOMMatrix(getComputedStyle(progress).transform);
          const remaining = duration * m.a;
          progress.style.transition = `transform ${remaining}ms linear`;
          requestAnimationFrame(() => { progress.style.transform = 'scaleX(0)'; });
          state.toastTimer = setTimeout(closeToast, remaining);
        } catch (e) { state.toastTimer = setTimeout(closeToast, duration); }
      });
    }
  }

  function closeToast() {
    if (state.toastTimer) { clearTimeout(state.toastTimer); state.toastTimer = null; }
    if (!state.toastEl) return;
    const el = state.toastEl;
    el.classList.remove('is-visible');
    setTimeout(() => { if (el.parentNode) el.remove(); }, 280);
    state.toastEl = null;
  }

  async function showSheet(data) {
    if (data.error) {
      sheet.innerHTML = `
        <div class="cart-notif__sheet-handle" data-sheet-close>
          <span class="cart-notif__sheet-handle-icon">⌄</span>
        </div>
        <div class="cart-notif__sheet-error">
          <div class="cart-notif__sheet-error-icon">!</div>
          <div class="cart-notif__sheet-error-title">${escapeHtml(config.msgErrorTitle)}</div>
          <div class="cart-notif__sheet-error-msg">${escapeHtml(data.errorMessage)}</div>
          <button type="button" class="cart-notif__sheet-close-btn" data-sheet-close>${escapeHtml(config.msgClose)}</button>
        </div>
      `;
    } else {
      const statusText = data.isNew ? config.msgAdded : config.msgUpdated.replace('{qty}', data.qty);
      const thumbHtml = data.product.image ? `<img src="${escapeHtml(data.product.image)}" alt="" loading="lazy">` : '';
      sheet.innerHTML = `
        <div class="cart-notif__sheet-handle" data-sheet-close>
          <span class="cart-notif__sheet-handle-icon">⌄</span>
        </div>
        <div class="cart-notif__sheet-header">
          <div class="cart-notif__sheet-thumb">${thumbHtml}</div>
          <div class="cart-notif__sheet-message">${escapeHtml(statusText)}</div>
          <button type="button" class="cart-notif__sheet-link" data-view-cart>${escapeHtml(config.msgViewCart)}</button>
        </div>
        <div class="cart-notif__sheet-divider"></div>
        <div class="cart-notif__sheet-recs">
          <div class="cart-notif__sheet-recs-title">${escapeHtml(config.msgRecsTitle)}</div>
          <div class="cart-notif__sheet-recs-scroll" data-recs-scroll>
            <div style="padding: 20px; color: var(--cn-fg-mute); font-size: 12px;">Loading...</div>
          </div>
        </div>
      `;
    }

    sheet.querySelectorAll('[data-sheet-close]').forEach(el => el.addEventListener('click', closeSheet));
    const viewBtn = sheet.querySelector('[data-view-cart]');
    if (viewBtn) viewBtn.addEventListener('click', () => { window.location.href = '/cart'; });
    backdrop.addEventListener('click', closeSheet, { once: true });

    requestAnimationFrame(() => {
      sheet.classList.add('is-visible');
      backdrop.classList.add('is-visible');
    });

    setupSwipeDown(sheet);
    bindEscClose();

    if (!data.error && data.product.productId) {
      loadRecommendations(data.product.productId);
    }
  }

  function closeSheet() {
    sheet.classList.remove('is-visible');
    backdrop.classList.remove('is-visible');
  }

  function closeAll() { closeToast(); closeSheet(); }

  function setupSwipeDown(sheetEl) {
    const handle = sheetEl.querySelector('.cart-notif__sheet-handle');
    if (!handle) return;
    let startY = 0, currentY = 0, isDragging = false;
    const onStart = (e) => {
      isDragging = true;
      startY = (e.touches ? e.touches[0].clientY : e.clientY);
      sheetEl.style.transition = 'none';
    };
    const onMove = (e) => {
      if (!isDragging) return;
      currentY = (e.touches ? e.touches[0].clientY : e.clientY);
      const dy = Math.max(0, currentY - startY);
      sheetEl.style.transform = `translateY(${dy}px)`;
    };
    const onEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      sheetEl.style.transition = '';
      const dy = currentY - startY;
      if (dy > 60) closeSheet();
      else sheetEl.style.transform = '';
    };
    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: true });
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('mousedown', onStart);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  }

  function bindEscClose() {
    const handler = (e) => {
      if (e.key === 'Escape') {
        closeAll();
        document.removeEventListener('keydown', handler);
      }
    };
    document.addEventListener('keydown', handler);
  }

  async function loadRecommendations(productId) {
    const scrollEl = sheet.querySelector('[data-recs-scroll]');
    if (!scrollEl) return;

    if (state.recsCache.has(productId)) {
      scrollEl.innerHTML = state.recsCache.get(productId);
      return;
    }

    try {
      // Section Rendering API로 카드 HTML 통째로 받기
      // → 가격 포맷, 통화, 배지, 별점 등 사이트와 100% 동일
      const url = `/recommendations/products?section_id=cart-notification-recs&product_id=${productId}&limit=${config.recsLimit}&intent=${config.recsIntent}`;
      const res = await fetch(url);

      let html = '';
      if (res.ok) {
        const fullHtml = await res.text();
        // Shopify가 section을 통째로 감싸는 <div id="shopify-section-...">를 풀어서 자식만 추출
        const wrapper = document.createElement('div');
        wrapper.innerHTML = fullHtml;
        const sectionRoot = wrapper.querySelector('.shopify-section') || wrapper.firstElementChild;
        html = sectionRoot ? sectionRoot.innerHTML : '';
      }

      // 결과 비어있으면 fallback 컬렉션으로 동일하게 시도
      if (!html.trim() && config.fallbackCollection) {
        try {
          const fbUrl = `/collections/${config.fallbackCollection}?section_id=cart-notification-recs`;
          const fbRes = await fetch(fbUrl);
          if (fbRes.ok) {
            const fbFullHtml = await fbRes.text();
            const fbWrap = document.createElement('div');
            fbWrap.innerHTML = fbFullHtml;
            const fbRoot = fbWrap.querySelector('.shopify-section') || fbWrap.firstElementChild;
            html = fbRoot ? fbRoot.innerHTML : '';
          }
        } catch (e) {
          console.warn('[cart-notification] fallback fetch failed:', e);
        }
      }

      if (!html.trim()) {
        const recsSection = sheet.querySelector('.cart-notif__sheet-recs');
        if (recsSection) recsSection.style.display = 'none';
        return;
      }

      state.recsCache.set(productId, html);
      scrollEl.innerHTML = html;
    } catch (err) {
      console.error('[cart-notification] recommendations fetch failed:', err);
      const recsSection = sheet.querySelector('.cart-notif__sheet-recs');
      if (recsSection) recsSection.style.display = 'none';
    }
  }

  function renderRecCard(product) {
    const onSale = product.compare_at_price && product.compare_at_price > product.price;
    const featuredImage = product.featured_image || (product.images && product.images[0]) || '';
    let imgHtml = '<div class="cart-notif__card-img-placeholder"></div>';
    if (featuredImage) {
      const src = typeof featuredImage === 'string' ? featuredImage : featuredImage.src;
      if (src) {
        const sizedSrc = src.includes('?') ? src + '&width=400' : src + '?width=400';
        imgHtml = `<img src="${escapeHtml(sizedSrc)}" alt="${escapeHtml(product.title || '')}" loading="lazy" class="cart-notif__card-img">`;
      }
    }
    const badges = [];
    if (product.available === false) {
      badges.push('<span class="cart-notif__card-badge cart-notif__card-badge--sold-out">SOLD OUT</span>');
    } else {
      const tagList = product.tags ? (Array.isArray(product.tags) ? product.tags : product.tags.split(',')) : [];
      if (tagList.some(t => t.trim().toLowerCase() === 'coupon')) {
        badges.push('<span class="cart-notif__card-badge cart-notif__card-badge--coupon">COUPON</span>');
      }
      if (onSale) {
        badges.push('<span class="cart-notif__card-badge cart-notif__card-badge--sale">SALE</span>');
      }
    }
    const priceClass = onSale ? '' : ' cart-notif__card-price--no-sale';
    const priceHtml = `<span class="cart-notif__card-price${priceClass}">${formatMoney(product.price * 100)}</span>` +
      (onSale ? `<span class="cart-notif__card-price-original">${formatMoney(product.compare_at_price * 100)}</span>` : '');
    return `
      <a href="${escapeHtml(product.url || ('/products/' + product.handle))}" class="cart-notif__card" data-rec-card>
        <div class="cart-notif__card-img-wrap">
          ${imgHtml}
          <button type="button" class="cart-notif__card-wishlist" aria-label="Wishlist" onclick="event.preventDefault(); event.stopPropagation();">♡</button>
          ${badges.length ? `<div class="cart-notif__card-badges">${badges.join('')}</div>` : ''}
        </div>
        ${product.vendor ? `<div class="cart-notif__card-brand">${escapeHtml(product.vendor)}</div>` : ''}
        <div class="cart-notif__card-name">${escapeHtml(product.title)}</div>
        <div class="cart-notif__card-price-row">${priceHtml}</div>
      </a>
    `;
  }

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (!isMobile() && sheet.classList.contains('is-visible')) closeSheet();
      if (isMobile() && state.toastEl) closeToast();
    }, 200);
  });

  log('cart-notification initialized', config);
})();
